import { createSelector } from 'reselect';
import { State } from 'reducers';
import { CellState, CellIndex, DeviceMap, Stream, StreamId, Camera } from 'types';
import { getDevices, getStreamFromDevicesAndStreamId, getCameraFromDevicesAndStreamId, isCamera } from './deviceSelectors';
import { makeStreamId, splitStreamId } from 'util/streamId';

export type HasCellIndex = {
    cellIndex: CellIndex | null;
};

const getCellIndex = <T extends HasCellIndex>(state: State, { cellIndex }: T) => cellIndex;


export const getLayout = (state: State) => state.monitoring.size;

export const getDate = (state: State) => state.monitoring.date;

export const getCells = (state: State) => state.monitoring.cells;

export const getActiveCellIndex = (state: State) => state.monitoring.activeCellIndex;

export const getFilterText = (state: State) => state.monitoring.filterText;

export const getTimelineLabelWidth = (state: State) => state.monitoring.labelWidth;

export const getCell: Reselect.OutputParametricSelector<State, HasCellIndex, CellState | null, (c: CellState[], i: CellIndex) => CellState | null> =
    createSelector(
        getCells,
        getCellIndex,
        (cells, cellIndex) => {
            if (!cells || typeof cellIndex !== 'number') return null;
            if (cellIndex < 0 || cellIndex > cells.length) return null;
            return cells[cellIndex];
        }
    )
;

export const isVideoConnecting =
    createSelector(
        getCell,
        cell => !!cell && cell.connecting
    )
;

export const isVideoConnected =
    createSelector(
        getCell,
        cell => !!cell && cell.connected
    )
;

export const isSeeking =
    createSelector(
        getCell,
        cell => !!cell && cell.seeking
    )
;

export const getVideoError =
    createSelector(
        getCell,
        cell => cell && cell.error
    )
;

export const getStillFrame =
    createSelector(
        getCell,
        cell => cell && cell.frame
    )
;

export const getPlaybackSpeed =
    createSelector(
        getCell,
        cell => cell ? cell.playbackSpeed : 0
    );

export const getStreamPosition =
    createSelector(
        getCell,
        cell => cell ? cell.streamPosition : 0
    );

export const isWaitingForDisconnectTime =
    createSelector(
        getCell,
        cell => cell ? cell.waitingForDisconnectTime : false
    );

export const isLive =
    createSelector(
        getCell,
        cell => !!cell && cell.live
    );

export const isPlaying =
    createSelector(
        getCell,
        cell => !!cell && cell.playing
    );

export const isMute =
    createSelector(
        getCell,
        cell => !!cell && cell.mute
    );

export const getMinutesPerSegment =
    createSelector(
        getCell,
        cell => cell ? cell.minutesPerSegment : 30
    );

export const getTimeline =
    createSelector(
        getCell,
        cell => cell && cell.timeline
    );

export const getScroll =
    createSelector(
        getCell,
        cell => cell ? cell.scroll : 1
    );

export const getSync =
    createSelector(
        getCell,
        cell => cell ? cell.sync : true
    );

export const getMonitoredDeviceIds =
    createSelector(
        getCells,
        cells => {
            if (!cells) return [];

            // Collect all the device IDs of streams the user is monitoring
            const deviceIds =
                cells
                    .filter(c => c.streamId)
                    .map(c => splitStreamId(c.streamId).deviceId);
            
            // Remove duplicate Device IDs and put in a predictable order
            return Object.keys(deviceIds.reduce((a, id) => ({ ...a, [id]: true }), {})).sort();
        }
    );
    
export const getStreamFromCell: Reselect.OutputParametricSelector<State, HasCellIndex, Stream | null, (dm: DeviceMap, c: CellState) => Stream | null> =
    createSelector(
        getDevices,
        getCell,
        (devices, cell) => {
            if (!devices) return null;
            if (!cell) return null;
            return getStreamFromDevicesAndStreamId(devices, cell.streamId);
        }
    )
;


export const getCameraFromCell: Reselect.OutputParametricSelector<State, HasCellIndex, Camera | null, (dm: DeviceMap, c: CellState) => Camera | null> =
    createSelector(
        getDevices,
        getCell,
        (devices, cell) => {
            if (!devices) return null;
            if (!cell) return null;
            return getCameraFromDevicesAndStreamId(devices, cell.streamId);
        }
    )
;


const noStreamIds: StreamId[] = [];
export const getFilteredStreamIds: Reselect.OutputSelector<State, StreamId[], (dm: DeviceMap, ft: string) => StreamId[]> =
    createSelector(
        getDevices,
        getFilterText,
        (devices, filterText) => {
            // Bail out if the devices haven't been loaded yet
            if (!devices) return noStreamIds;

            const re =
                filterText
                    ? new RegExp(filterText.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'i')
                    : null;

            // Find ids of devices that are cameras and match the filter text
            const deviceIds = Object.keys(devices).filter(id =>
                isCamera(devices[id]) &&
                (
                    !re ||
                    (re && re.test(devices[id].name)) ||
                    devices[id].deviceIndex.toString().indexOf(filterText) >= 0
                )
            );

            // Convert device ids into stream ids
            return deviceIds.reduce((allStreamIds, deviceId) => {
                const { streams } = devices[deviceId] as Camera;
                const myStreamIds = streams.map(s => makeStreamId(deviceId, s.streamIndex));
                return [ ...allStreamIds, ...myStreamIds ] ;
            }, [] as StreamId[]);
        }
    );

export const getActiveCellStreamId =
    createSelector(
        getActiveCellIndex,
        getCells,
        (cellIndex, cells) => {
            if (!cells || typeof cellIndex !== 'number') return null;
            if (cellIndex < 1 || cellIndex > cells.length || !cells[cellIndex]) return null;
            const { streamId } = cells[cellIndex];
            return streamId;
        }
    );



// WEBPACK FOOTER //
// ./src/selectors/monitoringSelectors.ts